import {useContext, useEffect, useRef} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import FdsModalContext from '../../../../../_fds/layout/ModalContext'
import {useToast} from '../../../../../_fds/layout/components/toast-messages'
import {useStore} from '../../store'
import {LaborTemplateModel, LaborTemplateShiftModel} from '../../core/_models'
import {createLaborTemplate, getLaborTemplate, updateLaborTemplate} from '../../core/_requests'
import {convertAvailablePositionsToOptions} from '../../helpers/generals'
import {
  DEFAULT_TEMPLATE_ROWS,
  buildTemplateDraft,
  eventsToTemplateShifts,
  getTemplateAnchorStart,
  makeTemplateRowResources,
  templateShiftsToEvents,
} from '../../helpers/templates'
import {FieldProps, TemplateDraft} from '../../types'

// The template grid rides on the scheduler route — the sibling tabs (overview,
// team members, availability) render their own page instead.
const SCHEDULE_PATH = '/labor-scheduling/scheduling/schedule'

// The editor in template mode: no assignee select (templates have no
// employees) — but employee_id must survive the editor round-trip as a hidden
// field, because the row a shift belongs to travels in event.employee_id.
const templateEditorFields = (availablePositions: any): FieldProps[] => [
  {name: 'employee_id', type: 'hidden'},
  {
    name: 'position',
    type: 'select',
    config: {label: 'Position', required: false},
    options: convertAvailablePositionsToOptions(availablePositions),
  },
]

/**
 * Template-mode lifecycle built on the singleton scheduler store.
 *
 * Entering snapshots the real schedule state (events, resources, fields, view,
 * dates) into `scheduleSnapshot` and swaps in the client-side draft; exiting
 * restores the snapshot in one batch — restoring instead of refetching matters
 * because `handleState` suppresses equal `selectedWeekStart` values, so a
 * refetch-triggering restore would never actually refetch.
 */
export const useTemplateMode = () => {
  const {
    events,
    resources,
    fields,
    view,
    selectedDate,
    selectedWeekStart,
    selectedLocationId,
    availablePositions,
    templateDraft,
    templateAnchorStart,
    templateDirty,
    templateMode,
    scheduleSnapshot,
    week,
    handleState,
    handleMultipleStates,
  } = useStore()
  const {setSettings} = useContext(FdsModalContext)
  const {showToast} = useToast()
  const navigate = useNavigate()
  const {pathname} = useLocation()

  const enterTemplateMode = (
    template: LaborTemplateModel | null,
    newDraft?: {name: string; template_type: 'daily' | 'weekly'}
  ) => {
    // Anchor on the COMPANY week start so the weekly tabs follow it.
    const anchor = getTemplateAnchorStart(week?.weekStartOn ?? 1)
    const draft: TemplateDraft = template
      ? buildTemplateDraft(template)
      : {
          uuid: null,
          name: newDraft?.name || 'New template',
          template_type: newDraft?.template_type || 'daily',
          rowCount: DEFAULT_TEMPLATE_ROWS,
        }
    const draftEvents = template ? templateShiftsToEvents(template, anchor) : []

    handleMultipleStates(
      new Map<string, any>([
        ['scheduleSnapshot', {events, resources, fields, view, selectedDate, selectedWeekStart}],
        ['templateMode', true],
        ['templateDraft', draft],
        ['templateAnchorStart', anchor],
        ['templateDirty', false],
        ['view', 'timeline'],
        ['selectedDate', anchor],
        ['fields', templateEditorFields(availablePositions)],
        ['events', draftEvents],
        ['resources', makeTemplateRowResources(draft.rowCount, draftEvents, anchor)],
      ])
    )

    // The toggle lives in the toolbar, which is shared by every scheduling tab
    // — entering from one of the others would swap in the template header and
    // leave that tab's page (team members, availability, …) below it. Batched
    // with the state update above, so the grid mounts straight into the draft.
    if (pathname !== SCHEDULE_PATH) {
      navigate(SCHEDULE_PATH)
    }
  }

  const openTemplate = async (uuid: string) => {
    const template = await getLaborTemplate(uuid)
    if (!template) {
      showToast('Error', 'Unable to load template!', 'danger')
      return
    }
    enterTemplateMode(template)
  }

  const exitTemplateMode = () => {
    const states = new Map<string, any>([
      ['templateMode', false],
      ['templateDraft', null],
      ['templateAnchorStart', null],
      ['templateDirty', false],
      ['scheduleSnapshot', null],
    ])
    if (scheduleSnapshot) {
      states.set('events', scheduleSnapshot.events)
      states.set('resources', scheduleSnapshot.resources)
      states.set('fields', scheduleSnapshot.fields)
      states.set('view', scheduleSnapshot.view)
      states.set('selectedDate', scheduleSnapshot.selectedDate)
      states.set('selectedWeekStart', scheduleSnapshot.selectedWeekStart)
    }
    handleMultipleStates(states)
  }

  // Dirty-aware exit: `andThen` runs after the mode is left (e.g. the deferred
  // location change or navigation the user attempted).
  const requestExit = (andThen?: () => void) => {
    if (!templateDirty) {
      exitTemplateMode()
      andThen?.()
      return
    }
    setSettings({
      cancelButtonText: 'Stay',
      saveButtonText: 'Discard and leave',
      saveButtonCallback: () => {
        exitTemplateMode()
        andThen?.()
      },
      closeButtonCallback: () => {},
      titleText: 'Unsaved template changes',
      content: (
        <div>
          <div className='text-gray-600 fs-6 fw-bold'>
            Your template has unsaved changes — everything will be gone if you leave.
          </div>
          <div className='text-danger fs-7 fw-bold'>
            Save the template first, or discard the changes.
          </div>
        </div>
      ),
      show: true,
    })
  }

  const saveTemplate = async (): Promise<boolean> => {
    if (!templateDraft || !selectedLocationId || !templateAnchorStart) return false
    const name = templateDraft.name.trim()
    if (!name) {
      showToast('Template Save Failed', 'Please give the template a name.', 'danger')
      return false
    }
    const shifts: LaborTemplateShiftModel[] = eventsToTemplateShifts(
      events,
      templateDraft.template_type,
      templateAnchorStart
    )
    try {
      let uuid = templateDraft.uuid
      if (uuid) {
        await updateLaborTemplate(uuid, {name, shifts})
      } else {
        const result = await createLaborTemplate({
          location_id: selectedLocationId,
          name,
          template_type: templateDraft.template_type,
          shifts,
        })
        uuid = result.uuid
      }
      handleMultipleStates(
        new Map<string, any>([
          ['templateDraft', {...templateDraft, name, uuid}],
          ['templateDirty', false],
        ])
      )
      showToast('Template Saved', `Template '${name}' saved successfully!`, 'success')
      return true
    } catch (error: any) {
      const message =
        error?.response?.status === 409
          ? 'A template with this name already exists for this store.'
          : 'Unable to save template!'
      showToast('Template Save Failed', message, 'danger')
      return false
    }
  }

  return {
    templateMode,
    templateDraft,
    templateAnchorStart,
    templateDirty,
    enterTemplateMode,
    openTemplate,
    exitTemplateMode,
    requestExit,
    saveTemplate,
  }
}

/**
 * Mounted once (from CreateSchedulePage): keeps template-row resources in sync
 * with the draft events, guards every escape path while the draft is dirty,
 * and resets the singleton store if the page unmounts mid-template.
 */
export const useTemplateModeEffects = () => {
  const {templateMode, templateDraft, templateDirty, events, selectedDate, handleState} = useStore()
  const {requestExit, exitTemplateMode} = useTemplateMode()
  const navigate = useNavigate()

  // Rows follow the draft: position labels update as shifts change, new rows
  // appear when rowCount grows, weekly tabs relabel rows per visible day.
  const rowCount = templateDraft?.rowCount
  useEffect(() => {
    if (!templateMode || !rowCount) return
    handleState(makeTemplateRowResources(rowCount, events, selectedDate), 'resources')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode, events, selectedDate, rowCount])

  // Tab close / refresh: the only interception the browser allows.
  useEffect(() => {
    if (!(templateMode && templateDirty)) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [templateMode, templateDirty])

  // In-app navigation: react-router 6.3 has no useBlocker, so intercept every
  // same-origin anchor click at the capture phase (covers header menu, sidebar
  // and any other <Link>), confirm, then navigate manually on discard.
  useEffect(() => {
    if (!(templateMode && templateDirty)) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link) return
      const href = link.getAttribute('href') || ''
      if (!href || href.startsWith('#')) return
      if (link.origin !== window.location.origin) return // full unload → beforeunload
      const path = link.pathname + link.search
      if (path === window.location.pathname + window.location.search) return
      e.preventDefault()
      e.stopPropagation()
      requestExit(() => navigate(path))
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode, templateDirty])

  // The store is a module singleton that outlives this page: if the component
  // tree unmounts while template mode is on (programmatic navigation the click
  // guard can't see), restore the schedule state so the next mount is sane.
  const activeRef = useRef(templateMode)
  activeRef.current = templateMode
  const exitRef = useRef(exitTemplateMode)
  exitRef.current = exitTemplateMode
  useEffect(() => {
    return () => {
      if (activeRef.current) {
        exitRef.current()
      }
    }
  }, [])
}
